import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react"
import Table from "react-bootstrap/esm/Table"
import { paginate, sort } from "@utils/ArrayUtils"
import CSPagination from "@components/Table/CSPagination"
import styles from '@components/Table/css/CSTable.module.scss'
import { getValueFromPath } from "cs.node.utils/ObjectUtils"
import { ContextMenuItem } from "@interfaces/ContextMenu"
import ContextMenu from "@components/ContextMenu/ContextMenu"
import { ListMetadata } from "cs.node.utils/csapi/provisioning"
import Localization from "@localization/Index"
import CSSpan from "@components/HTMLElements/CSSpan"
import { Spinner } from "react-bootstrap"
import useLongPress from "@hooks/UseLongPress"
import { useMobileMediaQuery } from "@utils/MediaComponents"

export interface Column {
    name: string | (()=>JSX.Element),
    field: string,
    render?: (row:any)=>JSX.Element,
    width?:number,
    align?: "start"|"center"|"end",
    sortable?: boolean,
    sortFunction?: (a:any, b:any)=>number
}

export const DEFAULT_PAGE_SIZE = 20

export type CSTableServerPagination = {
    _pageIndex: number
    _pageSize?: number
    _sort?: [number|undefined, "ASC"|"DESC"]
}

export type CSTableServerParams = Record<string, unknown>

export type CSTableServerResponse = {
    _metadata: ListMetadata,
    content: Object[]
}

export type CSTableServerRequest = CSTableServerPagination & CSTableServerParams

export type CSTableRef = {
    getTableState: ()=>TableStates
    reload: Function
}

export enum TableStates {
    INIT = "INIT",
    NO_DATA = "NO_DATA",
    LOADING = "LOADING",
    ERROR = "ERROR",
    SHOWN_DATA = "SHOWN_DATA"
}

export const emptyTableResponse:CSTableServerResponse = {
    _metadata: { "page_index": 0,"page_size": 20,"total_pages": 1, "total_items": 0},
    content:  []
}

type ServerProps = {
    server: true
    client?: undefined
    fetchData: (request?:CSTableServerRequest)=>Promise<CSTableServerResponse>
}

type ClientProps = {
    client: true
    server?: undefined
    loading: boolean
    data: Array<object>
    totalEntries: number
}

type Props = {
    striped?: boolean
    bordered?: boolean
    hover?: boolean
    small?: boolean
    dark?: boolean
    enableGotoPage?: "OFF"|"CHANGE"|"BUTTON"
    contextMenu?: Array<ContextMenuItem> | ((row:any, e?: React.MouseEvent<unknown>) => Array<ContextMenuItem>)
    columns: Array<Column>
    pagination?: "NONE" | "START" | "END" | "BOTH"
    height?: string
    pageSizeSelect?: Array<number>, // if undefined, it's as enablePageSize = false
    data_cy?:string,
    //bottomOffset?: number,
    defaultSorting?: [index: number, direction: "ASC" | "DESC"]
    noResultsLabel?: string
    ready?: boolean
} &  (
        | ServerProps
        | ClientProps
    )

const CSTable = forwardRef((props:Props, ref) => {
    const { striped, bordered, hover, small, dark, enableGotoPage="BUTTON", noResultsLabel=Localization.TABLE.NESSUN_RISULTATO, contextMenu, columns, pagination="NONE", height, pageSizeSelect, data_cy, defaultSorting, ready=true } = props

    const size = small?"sm":undefined
    const variant = dark?"dark":undefined

    const [currentPage, setCurrentPage] = useState(1)
    const [mouseCoordinates, setMouseCoordinates] = useState({x:0,y:0})
    const [contextMenuOpen, setContextMenuOpen] = useState(false)
    const [selectedRow, setSelectedRow] = useState<number|undefined>(undefined)
    const [sortedColumn, setSortedColumn] = useState<number|undefined>(defaultSorting?.[0])
    const [sortedDir, setSortedDir] = useState<"ASC"|"DESC">(defaultSorting?.[1] ?? "ASC")
    const [selectedPageSize, setSelectedPageSize] = useState(pageSizeSelect?.[0])
    const [currentData, setCurrentData] = useState<Object[]>([])
    const [tableState, setTableState] = useState(TableStates.INIT)
    const [totalPages, setTotalPages] = useState(0)
    const [totalServerItems, setTotalServerItems] = useState(0)
    const {fetchData, server } = props as ServerProps
    const isMobile = useMobileMediaQuery()
    const getLongPressHandlers = useLongPress((e, rowIndex) => {
        openContextMenuMobile(e, rowIndex)
    }, 400)

    const {data, totalEntries, client, loading} = props as ClientProps
    const loadingData = (client)?loading:tableState === TableStates.LOADING

    const serverRequest: CSTableServerPagination = useMemo(()=>({
        _sort: [sortedColumn, sortedDir],
        _pageIndex: currentPage-1,
        _pageSize: selectedPageSize
    }), [currentPage, selectedPageSize, sortedColumn, sortedDir])

    // the function passed in the props must return data as an array of object (every object will be the row, every prop will be the col)
    const loadServerData = useCallback(async ()=>{
        try {
            setTableState(TableStates.LOADING)
            const response = await fetchData(serverRequest)
            const { _metadata, content  } = response
            const { total_items, total_pages } = _metadata
            // set the metadata in corresponding states
            setTotalPages(total_pages ?? 0)
            setTotalServerItems(total_items ?? 0)
            setCurrentData(content)
            setTableState((total_items ?? 0)===0?TableStates.NO_DATA: TableStates.SHOWN_DATA)
        } catch(e:any) {
            if(e.name === "CanceledError") {
                return setTableState(TableStates.LOADING)
            }
            return setTableState(TableStates.ERROR)
        }
    }, [serverRequest, fetchData])
    
    const loadClientData = useCallback(()=>{
        // just put the data in the currentData state (as is coming from the props by the outer page that use the Table component)
        setCurrentData(data)
        setTotalPages(data.length / (selectedPageSize ?? data.length)) // if the selected page size is undefined, the number of pages is 1
        setTableState((data.length ?? 0)===0?TableStates.NO_DATA: TableStates.SHOWN_DATA)
        return
    }, [data, selectedPageSize])

    const loadData = useCallback(()=>{
        return (server)?loadServerData():loadClientData()
    }, [server, loadServerData, loadClientData])

    useImperativeHandle(ref, () => {
        return {
            getTableState: ()=>{
                return tableState
            },
            reload: (initialPage?: number)=>{
                if(initialPage) {
                    setCurrentPage(initialPage+1)
                } else {
                    setCurrentPage(1)
                }
                return loadData() 
            }
        };
    }, [tableState, loadData]);

    /**
     * get the default action from the context menu and execute the corresponding action
     * @param row the data to pass to the action
     */
    const executeDefaultAction = (row:any, e: React.MouseEvent<unknown>) =>{
        const _contextMenu = (typeof contextMenu==="function")?contextMenu(row, e):contextMenu

        if(_contextMenu) {   
            const defaultAction = _contextMenu.find(item=>item.default) ?? _contextMenu[0]
            if(defaultAction) {
                defaultAction.action?.(row)
            }
        }
    }

    /**
     * Open the context menu
     * @param e the event that triggered the opening
     * @param rowIndex the row index of the visible data with which to select the current row for the actions in the context menu
     */
    const openContextMenu = (e:React.MouseEvent, rowIndex: number) =>{
        if(contextMenu){
            e.preventDefault()
            setMouseCoordinates({x:e.pageX, y:e.pageY})
            setContextMenuOpen(true)
            setSelectedRow(rowIndex)
        }
    }

    /**
     * Open the context menu in mobile, with a long press on the row
     * @param e the event that triggered the opening
     * @param rowIndex the row index of the visible data with which to select the current row for the actions in the context menu
     */
    const openContextMenuMobile = (e:React.TouchEvent, rowIndex: number) =>{
        if(contextMenu){
            e.preventDefault()
            setMouseCoordinates({x:e.touches[0].pageX, y:e.touches[0].pageY})
            setContextMenuOpen(true)
            setSelectedRow(rowIndex)
        }
    }

    const toggleSort = (index:number) =>{
        setSortedColumn((prev)=>{
            if(prev===undefined || prev!==index) {
                setSortedDir("ASC")
            } else {
                setSortedDir((prevDir)=>{
                    return prevDir==="ASC"?"DESC":"ASC"
                })
            }
            return index
        })
    }

    useEffect(()=>{
        client && loadClientData()
    },[client, loadClientData])

    useEffect(()=>{
        server && ready && tableState !== TableStates.INIT && loadServerData()
    }, [sortedColumn, sortedDir, selectedPageSize, currentPage, data_cy, server, ready])

    // if the pagination is serverside, simply return data
    let sortedData =  structuredClone(currentData)
    if(sortedColumn!==undefined && sortedColumn < columns.length) {
        const sortColumn = columns[sortedColumn]
        sortedData = sort(sortedData, sortColumn.field, sortedDir, sortColumn.sortFunction) as Array<object>
    }
    const visibleData:Array<object>|undefined = (server)?currentData:(paginate(sortedData, selectedPageSize, currentPage) as Array<object>)
    const totalFilteredPages = (server)?totalPages:Math.ceil((currentData.length)/(selectedPageSize ?? currentData.length))
    const totalItems = (server)?totalServerItems: totalEntries

    const selectedRowContent = (!!visibleData && selectedRow!==undefined)?visibleData[selectedRow]:undefined

    const _contextMenu = (typeof contextMenu==="function")?contextMenu(selectedRowContent):contextMenu

    const currentContextMenu = _contextMenu?.map(item=>{
        const { show=true, text, disabled } = item

        return {
            text: ()=>(typeof text=="string")?text:(typeof text === "function"?text(selectedRowContent):text),
            default: item.default,
            action: ()=>item.action?.(selectedRowContent),
            show: ()=>(typeof show=="boolean")?show:show?.(selectedRowContent),
            disabled,
            subMenu: item.subMenu?{
                items: item.subMenu.items.map(subItem=>{
                    const { show:subShow=true, text:subText, action:subAction, disabled} = subItem
                    return {
                        text: ()=>(typeof subText=="string")?subText:(typeof subText === "function"?subText(selectedRowContent):subText),
                        action: ()=>subAction?.(selectedRowContent),
                        show: ()=>(typeof subShow=="boolean")?subShow:subShow?.(selectedRowContent),
                        disabled
                    }
                })
            }:undefined
        }
    })

    return (
        <>
            {["BOTH", "START"].includes(pagination) &&
                <CSPagination 
                    client={client}
                    pageIndex={currentPage}
                    pageSize={selectedPageSize}
                    pageSizeSelect={pageSizeSelect}
                    totalItems={totalItems}
                    totalFiltered={client?(currentData?.length ?? 0):totalItems}
                    totalFilteredPages={totalFilteredPages}
                    onPageChange={(page)=>setCurrentPage(page)}
                    onPageSizeChange={(pageSize)=>setSelectedPageSize(pageSize)}
                    enableGotoPage={enableGotoPage}
                    />
            }
            <div className="overflow-auto" style={{height}}>
                <Table className={`${styles.table} ${isMobile ? styles.mobile : ""}`} striped={striped} bordered={bordered} hover={hover} size={size} variant={variant} >
                    <thead>
                        <tr>
                            {columns.map((column,index)=>{
                                const {align="start", sortable=true, name:Name, width=100/columns.length } = column
                                let paddingClass = "px-3"
                                if(align==="start" && index!==columns.length-1) paddingClass="pe-3"
                                if(align==="end" && index!==0) paddingClass="ps-3"
                                
                                return <th key={`th_${index}`} role={sortable?"button":undefined} onClick={()=>{if(sortable) {toggleSort(index)}}} className={`text-${align}`} style={{width: `${width}%`}}>
                                    <div className={`d-flex align-items-start justify-content-${align} ${paddingClass}`}>
                                        {typeof Name === "string" ? <span className="">{Name}</span> : <Name />}
                                        {sortable && sortedColumn===index && sortedDir==="DESC" && <span className="ms-2"><i className="small bi-chevron-down" /></span>}
                                        {sortable && sortedColumn===index && sortedDir==="ASC" && <span className="ms-2"><i className="small bi-chevron-up" /></span>}
                                    </div>
                                    </th>
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {loadingData && <>
                            <tr>
                                <td colSpan={100}>
                                    <div style={{marginTop: '40px', marginBottom: '40px'}} className="w-100 d-flex align-items-center justify-content-center">
                                        <Spinner animation="border" variant="primary" style={{width: '40px', height: "40px"}} />
                                    </div>
                                </td>
                            </tr>
                        </>}
                        {!loadingData && !((currentData ?? []).length>0) && tableState === TableStates.NO_DATA &&
                            <tr>
                                <td colSpan={columns.length} className="text-center">
                                    <p>{noResultsLabel}</p>
                                </td>
                            </tr>
                        }
                        {!loadingData && tableState === TableStates.ERROR &&
                            <tr>
                                <td colSpan={columns.length} className="text-center">
                                    <div className="dlf-ex flex-column justify-content-start align-items-center">
                                        <p>{Localization.TABLE.SI_E_VERIFICATO_UN_ERRORE}</p>
                                        <CSSpan role="button" className="mt-2" onClick={()=>{loadData()}}>
                                            <i className="bi-arrow-counterclockwise font-2x text-primary" />
                                        </CSSpan>
                                    </div>
                                </td>
                            </tr>
                        }
                        {!loadingData && !!visibleData && visibleData.map((row, rowIndex:number)=>{ 
                            return (
                                <tr key={`tr_${rowIndex}`} className={`${selectedRow===rowIndex?"table-primary": ""}`}>
                                    {columns.map((column,index)=>{
                                        const {render:Render, field="", align="start", width=100/columns.length} = column
                                        let paddingClass = "px-3"
                                        if(align==="start" && index!==columns.length-1) paddingClass="pe-3"
                                        if(align==="end" && index!==0) paddingClass="ps-3"

                                        const value=getValueFromPath(row, field) as string
                                        return (
                                        <td 
                                            role="button"
                                            onClick={(e)=>{
                                                e.preventDefault()
                                                executeDefaultAction(row, e)
                                            }}
                                            onContextMenu={(e)=>{
                                                openContextMenu(e, rowIndex)
                                            }}
                                            key={`td_${index}`}
                                            className={`text-${align}`}
                                            width={`${width}%`}
                                            {...getLongPressHandlers(rowIndex)}
                                        >
                                            <div className={paddingClass}>
                                                {Render && <Render {...row} />}
                                                {!Render && value}
                                            </div>
                                        </td>
                                        )
                                    })}
                                </tr>
                            )
                        })}
                    </tbody>
                </Table>
            </div>
            {["BOTH", "END"].includes(pagination) &&
                <CSPagination 
                    client={client}
                    pageIndex={currentPage}
                    pageSize={selectedPageSize}
                    pageSizeSelect={pageSizeSelect}
                    totalItems={totalItems}
                    totalFiltered={client?(currentData?.length ?? 0):totalItems}
                    totalFilteredPages={totalFilteredPages}
                    onPageChange={(page)=>setCurrentPage(page)}
                    onPageSizeChange={(pageSize)=>setSelectedPageSize(pageSize)}
                    enableGotoPage={enableGotoPage}
                    disabled={loadingData}
                    />
            }
            {contextMenuOpen && currentContextMenu && selectedRow!==undefined && <ContextMenu coordinates={mouseCoordinates} menu={currentContextMenu} closeFunction={()=>{setContextMenuOpen(false);setSelectedRow(undefined)}}/>}
            </>
    )
})

export default CSTable